/**
 * Workshop version of HtmlFrame.
 *
 * Props-based, no React Flow or store dependencies.
 * Renders an iframe with sandboxed HTML content.
 */

import { useState, useEffect } from "react";
import { Icons } from "@/components/ui/icons";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";
import { statusColor } from "@/workshop/components/shapes/shared/frame-utils";

const DARK_BASE_STYLE = `<style>:root{color-scheme:dark}body{background:#1C1C1E!important;color:#F5F5F7!important}</style>`;

interface HtmlFrameProps {
  title: string;
  html: string;
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  onCopy?: () => void;
  onResizeStart?: React.MouseEventHandler;
}

export function HtmlFrame({
  title,
  html,
  status,
  agentName,
  onCopy,
  onResizeStart,
}: HtmlFrameProps) {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const el = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(el.classList.contains("dark"));
    });
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const themedHtml = isDark ? DARK_BASE_STYLE + html : html;

  return (
    <div
      className="denker-glass-frame flex flex-col rounded-frame overflow-hidden"
      data-testid="html-frame"
    >
      <FrameHeader
        title={title}
        icon={Icons.Globe}
        accentColor="bg-frame-html"
        agentName={agentName}
        statusColor={statusColor(status)}
        data-testid="html-frame-header"
      />

      <div className="overflow-hidden" style={{ height: 200, minHeight: 160 }}>
        {html ? (
          <iframe
            srcDoc={themedHtml}
            sandbox="allow-scripts"
            className="w-full h-full border-0"
            title={title}
            data-testid="html-frame-iframe"
          />
        ) : (
          <div className="flex items-center justify-center h-full px-3 py-2">
            <p className="italic text-muted text-canvas-base">No HTML content yet</p>
          </div>
        )}
      </div>

      <FrameFooter
        onCopy={onCopy}
        onResizeStart={onResizeStart}
        data-testid="html-frame-footer"
      />
    </div>
  );
}
